import { Card, Flex, Typography } from 'antd';
import type { CSSProperties } from 'react';
import { CaseInfo, PreSalesQuestionnaireContext } from '../../../dto/model';
import { strings } from '../../../lang';

const DISPLAY_VALUE_FALLBACK = '—';

export const formatPreSalesCustomerName = (
  customer: Pick<CaseInfo, 'salutation' | 'firstName' | 'lastName'>,
) =>
  [customer.salutation, customer.firstName, customer.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ') || DISPLAY_VALUE_FALLBACK;

export const formatPreSalesAddressLines = (
  customer: Pick<CaseInfo, 'street' | 'houseNumber' | 'zipcode' | 'city'>,
) => {
  const streetLine = [customer.street, customer.houseNumber]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  const cityLine = [customer.zipcode, customer.city]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');

  return [streetLine, cityLine].filter(Boolean);
};

type PreSalesDealInfoCardProps = {
  caseInfo: CaseInfo;
  context?: PreSalesQuestionnaireContext;
  isAdCase?: boolean;
};

const displayValue = (value?: string) => value?.trim() || DISPLAY_VALUE_FALLBACK;
const definitionValueStyle: CSSProperties = { margin: 0, overflowWrap: 'anywhere' };
const { Text } = Typography;

export const PreSalesDealInfoCard = ({
  caseInfo,
  context,
  isAdCase = false,
}: PreSalesDealInfoCardProps) => {
  const addressLines = formatPreSalesAddressLines(caseInfo);

  return (
    <Card aria-labelledby="bp-presales-deal-info-title" size='small'>
      <Flex vertical gap={16}>
        <Text id="bp-presales-deal-info-title" strong style={{ margin: 0 }} type='secondary'>
          {strings.preSalesQuestionnaire.dealInfoTitle.toUpperCase()}
        </Text>
        <Flex component="dl" vertical gap={12} style={{ margin: 0 }}>
          {!isAdCase && (
            <div>
              <dt><Text type="secondary">{strings.preSalesQuestionnaire.dealIdLabel}</Text></dt>
              <dd style={definitionValueStyle}>
                <Text strong>{displayValue(context?.crmId)}</Text>
              </dd>
            </div>
          )}
          <div>
            <dt><Text type="secondary">{isAdCase ? strings.adBookingPage.signupIdLabel : strings.preSalesQuestionnaire.hpOrderNumberLabel}</Text></dt>
            <dd style={definitionValueStyle}>
              <Text strong>{displayValue(isAdCase ? caseInfo.signupId : context?.hpOrderNumber)}</Text>
            </dd>
          </div>
          <div>
            <dt><Text type="secondary">{strings.preSalesQuestionnaire.addressLabel}</Text></dt>
            <dd style={definitionValueStyle}>
              {addressLines.length > 0
                ? (
                  <Flex vertical>
                    {addressLines.map((line) => <Text key={line} strong>{line}</Text>)}
                  </Flex>
                )
                : <Text strong>{DISPLAY_VALUE_FALLBACK}</Text>}
            </dd>
          </div>
          <div>
            <dt><Text type="secondary">{strings.preSalesQuestionnaire.emailLabel}</Text></dt>
            <dd style={definitionValueStyle}>
              <Text strong>{displayValue(caseInfo.email)}</Text>
            </dd>
          </div>
        </Flex>
      </Flex>
    </Card>
  );
};
